import { useState, useEffect, useCallback } from 'react';
import { hitsApi } from '../api';
import type { Hit, HitResult, RecordHitInput } from '../types';

export function useHits(boardId: string, boardStatus?: string) {
  const [hits, setHits] = useState<Hit[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Draft boards cannot have hits - skip the API call
  const isDraft = boardStatus === 'draft';

  const fetchHits = useCallback(async () => {
    if (!boardId) return;

    // Draft boards can't have hits, so don't call the API
    if (isDraft) {
      setHits([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const data = await hitsApi.getAll(boardId);
      setHits(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch hits');
    } finally {
      setIsLoading(false);
    }
  }, [boardId, isDraft]);

  useEffect(() => {
    fetchHits();
  }, [fetchHits]);

  const recordHit = async (input: RecordHitInput): Promise<HitResult> => {
    const result = await hitsApi.record(boardId, input);
    await fetchHits();
    return result;
  };

  return {
    hits,
    isLoading,
    error,
    fetchHits,
    refetch: fetchHits,
    recordHit,
  };
}
